import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

export default function AddModal({ open, handleClose, handleSnack, onSuccess, trajets }) {
  const [formData, setFormData] = useState({
    ID_Trajet: "",
    name: "",
    latitude: "",
    longitude: "",
    status: "active",
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  
  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);
  
  const resetForm = () => {
    setFormData({
      ID_Trajet: "",
      name: "",
      latitude: "",
      longitude: "",
      status: "active",
    });
    setErrors({});
    setGeneralError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    
    // Clear error for this field when user changes it
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
    
    // Clear general error when any field changes
    if (generalError) {
      setGeneralError("");
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Validate required fields based on PickupController.php rules
    if (!formData.name) {
      newErrors.name = "Le nom est requis.";
    }
    
    // Validate numeric fields
    if (formData.latitude && isNaN(Number(formData.latitude))) {
      newErrors.latitude = "La latitude doit être un nombre.";
    }
    
    if (formData.longitude && isNaN(Number(formData.longitude))) {
      newErrors.longitude = "La longitude doit être un nombre.";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    // Client-side validation
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setGeneralError("");
      
      // Prepare data for API
      const apiData = {
        ID_Trajet: formData.ID_Trajet,
        name: formData.name,
        latitude: formData.latitude,
        longitude: formData.longitude,
        Status: formData.status
      };
      
      // Send data to API
      await api.post("/pickups", apiData);
      
      // Show success message
      handleSnack("Point de ramassage ajouté avec succès", "success");
      
      // Refresh list and close modal
      if (onSuccess) onSuccess();
      handleClose();
    } catch (error) {
      console.error("Error adding pickup:", error);
      
      // Handle different types of errors
      if (error.response) {
        const { status, data } = error.response;
        
        // Handle validation errors (400 Bad Request)
        if (status === 400 && data) {
          const fieldErrors = {};
          
          // Map backend validation errors to form fields
          Object.entries(data).forEach(([key, value]) => {
            fieldErrors[key] = Array.isArray(value) ? value[0] : value;
          });
          
          setErrors(fieldErrors);
        }
        // Handle validation errors (422 Unprocessable Entity)
        else if (status === 422 && data.errors) {
          const fieldErrors = {};
          
          // Map backend validation errors to form fields
          Object.entries(data.errors).forEach(([key, value]) => {
            fieldErrors[key] = Array.isArray(value) ? value[0] : value;
          });
          
          setErrors(fieldErrors);
        }
        // Handle server errors (500 Internal Server Error)
        else if (status === 500) {
          setGeneralError("Une erreur serveur s'est produite. Veuillez réessayer plus tard.");
        }
        else {
          setGeneralError("Une erreur s'est produite lors de l'ajout du point de ramassage.");
        }
      } else {
        // Handle network errors
        setGeneralError("Problème de connexion au serveur. Veuillez vérifier votre connexion internet.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      height: 35,
      '&:hover fieldset': { borderColor: '#000' },
      '&.Mui-focused fieldset': { borderColor: 'red' }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 400, mr: 3 }}>
        AJOUTER UN POINT DE RAMASSAGE
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}
        <Typography variant="subtitle2">Trajet</Typography>
        <FormControl fullWidth sx={inputStyle} error={Boolean(errors.ID_Trajet)}>
          <Select name="ID_Trajet" value={formData.ID_Trajet} onChange={handleChange}>
            <MenuItem value="">Sélectionner un trajet</MenuItem>
            {trajets && trajets.map((trajet) => (
              <MenuItem key={trajet.ID_Trajet} value={trajet.ID_Trajet}>
                {trajet.name}
              </MenuItem>
            ))}
          </Select>
          {errors.ID_Trajet && <FormHelperText error>{errors.ID_Trajet}</FormHelperText>}
        </FormControl>
        <Typography variant="subtitle2">name</Typography>
        <TextField 
          fullWidth 
          name="name" 
          value={formData.name} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={Boolean(errors.name)}
          helperText={errors.name}
        />
        <Typography variant="subtitle2">Latitude</Typography>
        <TextField 
          fullWidth 
          name="latitude" 
          value={formData.latitude} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={Boolean(errors.latitude)}
          helperText={errors.latitude}
        />
        <Typography variant="subtitle2">Longitude</Typography>
        <TextField 
          fullWidth 
          name="longitude" 
          value={formData.longitude} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={Boolean(errors.longitude)}
          helperText={errors.longitude}
        />
        <Typography variant="subtitle2">Statut</Typography>
        <RadioGroup row name="status" value={formData.status} onChange={handleChange}>
          <FormControlLabel value="active" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Actif" />
          <FormControlLabel value="inactive" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Inactif" />
        </RadioGroup>
      </DialogContent>
      <Divider sx={{ mb: 1 }} />
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} startIcon={<CloseIcon />} onClick={handleClose}>
          Close
        </Button>
        <Button 
          variant="outlined" 
          onClick={handleSubmit}
          disabled={loading}
          sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} 
          startIcon={<CheckIcon />}
        >
          {loading ? "Enregistrement..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
